@use '@/styles/tokens' as *;

/* Figma 823:702498 — a 40px head with an 18px count badge overlapping its upper right.
 * <MapOverlay> places its box's top-left on the projected point, so the translate is what centres
 * the HEAD — not the badge-inflated box — on the cluster's coordinates, exactly as `.stack` does in
 * device-markers.module.scss.
 *
 * The radius here is the FIELD head's ($radius-sm, matching <FieldAreaSymbol>); `.round` below
 * overrides it for a device cluster. Carrying the radius on the button rather than on the head is
 * what lets the focus ring and the hover ring follow each head's own shape.
 *
 * The 40px is MARKER_PIN_SIZE_PX in src/lib/maps/markers.ts, which is also what cluster-markers.tsx
 * passes to <FieldAreaSymbol> one line away, and what the clustering radius itself is derived from
 * so that "markers that would visually overlap" means this box. SCSS cannot import TS, so the two
 * sides name each other — the same arrangement device-markers.module.scss already uses. Changing it
 * there must change it here. */
.cluster {
  @include reset-button;
  @include focus-ring;

  position: absolute;
  display: block;
  width: 40px;
  height: 40px;
  border-radius: $radius-sm;
  transform: translate(-50%, -50%);
  /* <MapOverlay>'s box takes no pointer events so a pan drag over empty space still reaches the
   * map; this control opts back in. */
  pointer-events: auto;
  cursor: pointer;
}

/* A DEVICE cluster's head is a circle, Figma 823:702487. */
.round {
  border-radius: 50%;
}

/* 113586: "hovering a cluster shows a pointer cursor and a hover state". No Figma frame draws it, so
 * this is depth only in the shared overlay token — the rule ticket 05 set for the Boundary hover, so
 * hover can never be read as a different kind of cluster. */
.cluster:hover {
  box-shadow: 0 0 0 4px var(--overlay-20);
}

/* A device cluster's head IS the device pin (Figma 823:702487), which is what makes a cluster read
 * as "these are devices" — so this rule is byte-identical to `.pin` in device-markers.module.scss
 * **on purpose**, and the two must be changed together.
 *
 * `composes: pin from '...'` would enforce that in one line and does build. Deliberately not used:
 * it would be the repo's first `composes`, and `css: false` in both vitest projects means no test
 * can ever exercise it — a mechanism only the production build sees. The house pattern for a value
 * one module owns and another repeats is to repeat it and say so (see the 56px hit box in
 * device-markers.module.scss), so this follows that rather than introducing a second pattern for the
 * same problem. */
.pin {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: $color-white-100;
  background: $color-error-700;
  border-radius: 50%;
}

/* Figma 823:702497 / 823:702484 — an 18px Overlay/Black/80 disc clearing the head's top and right
 * edges by 4px (the frame is 44 wide against a 40px head, with the head pushed down 4px).
 * Figma 823:702485 binds Main Font-Geist/11/M and Foundation /White/white-100. */
.count {
  position: absolute;
  top: -4px;
  right: -4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--overlay-80);
  font-size: $font-size-11;
  font-weight: $font-weight-medium;
  line-height: 1;
  color: $color-white-100;
}
